import Button from '@atlaskit/button';
import AdsCommentIcon from '@atlaskit/icon/core/comment';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { useHoverState } from '@databases/react';
import React, { type FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import AddCommentIcon from './icons/AddCommentIcon.tsx';
import CommentIcon from './icons/CommentIcon.tsx';

const ViewThreadButtonStyles = xcss({
  ':hover': {
    cursor: 'pointer'
  },
  borderRadius: 'radius.small',
  paddingInline: 'space.025'
});

const ViewThreadButtonNotSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.neutral.hovered'
  }
});

const FloatingThreadButtonStyles = xcss({
  backgroundColor: 'elevation.surface.overlay',
  boxShadow: 'elevation.shadow.raised',
  display: 'inline-flex',
  padding: 'space.0',
  borderRadius: 'radius.medium',
  overflow: 'hidden'
});

const ViewThreadButtonSelectedStyles = xcss({
  ':hover': {
    backgroundColor: 'color.background.accent.blue.subtlest'
  }
});

const AddThreadButtonStyles = xcss({
  borderRadius: 'radius.small',
  ':hover': {
    cursor: 'pointer'
  }
});

export interface CellCommentsButtonProps {
  isInActionGroup?: boolean;
  onClick: (e: React.MouseEvent) => void;
  commentToggled: boolean;
  hasThreads: boolean;
  commentCount: number;
  shouldShowCommentButton: boolean;
  shouldShowHoverButton: boolean;
  valueId: string;
}

const CellCommentsButton: FunctionComponent<CellCommentsButtonProps> = ({
  isInActionGroup = false,
  onClick,
  commentToggled,
  hasThreads,
  commentCount,
  shouldShowCommentButton,
  shouldShowHoverButton,
  valueId
}) => {
  const { formatMessage } = useIntl();
  const [isHovering, hoverProps] = useHoverState();

  const commentString = useMemo(() => {
    return commentCount > 9 ? '9+' : commentCount.toString();
  }, [commentCount]);

  const additionalThreadButtonStyles = useMemo(() => {
    return commentToggled
      ? ViewThreadButtonSelectedStyles
      : ViewThreadButtonNotSelectedStyles;
  }, [commentToggled]);

  if (!shouldShowCommentButton) {
    return null;
  }

  if (hasThreads) {
    const useFloatingAppearance =
      isInActionGroup ||
      getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
        fireExperimentExposure: false
      });

    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.show-comment-thread-button-tooltip'], {
          isMulti: commentCount
        })}
      >
        {useFloatingAppearance ? (
          <Box
            xcss={
              isInActionGroup ? xcss({ display: 'flex' }) : FloatingThreadButtonStyles
            }
            testId="floating-cell-comment-indicator"
          >
            <Button
              spacing="compact"
              appearance="subtle"
              iconBefore={
                <AdsCommentIcon
                  label=""
                  size="small"
                  color={token('color.icon.subtle')}
                />
              }
              onClick={onClick}
              isSelected={commentToggled}
              aria-expanded={commentToggled}
              aria-label={formatMessage(
                messages['comments.show-comment-thread-button-tooltip'],
                { isMulti: commentCount }
              )}
              style={{
                boxSizing: 'border-box',
                height: 22,
                minWidth: 30,
                font: token('font.body'),
                alignItems: 'center',
                columnGap: token('space.025'),
                paddingBlock: 0,
                paddingInline: token('space.050'),
                borderRadius: token('radius.medium'),
                color: token('color.icon.subtle')
              }}
              id={`cell-view-thread-button-${valueId}`}
              testId="cell-view-thread-button"
            >
              {commentString}
            </Button>
          </Box>
        ) : (
          <Box
            onClick={onClick}
            xcss={[ViewThreadButtonStyles, additionalThreadButtonStyles]}
            id={`cell-view-thread-button-${valueId}`}
            testId="cell-view-thread-button"
          >
            <Inline
              space="space.075"
              alignBlock="center"
              xcss={xcss({
                color: commentToggled ? 'color.text.accent.blue' : 'color.text.subtle'
              })}
            >
              <CommentIcon
                label={formatMessage(
                  messages['comments.view-comment-thread-button-label']
                )}
                color={
                  commentToggled
                    ? token('color.text.accent.blue', '#0055CC')
                    : token('color.text.subtle', '#44546F')
                }
              />
              {commentString}
            </Inline>
          </Box>
        )}
      </Tooltip>
    );
  }

  if (shouldShowHoverButton) {
    const shouldRemoveAddCommentButton = getExperimentValue(
      'databasesCellHoverNoiseReduction',
      'isEnabled',
      { fireExperimentExposure: true }
    );

    if (shouldRemoveAddCommentButton) {
      return null;
    }

    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.add-comment-thread-button-label'])}
      >
        <Box
          xcss={AddThreadButtonStyles}
          onClick={onClick}
          {...hoverProps}
          id={`cell-add-thread-button-${valueId}`}
          testId="cell-add-thread-button"
        >
          <AddCommentIcon
            label={formatMessage(messages['comments.add-comment-thread-button-label'])}
            color={
              isHovering
                ? token('color.text.subtle', '#44546F')
                : token('color.text.subtlest', '#626F86')
            }
          />
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default CellCommentsButton;
